/**
 * Global error handler — ловит uncaught errors которые не React render
 * (e.g. promise rejections, event handlers, requestAnimationFrame
 * callbacks, async operations).
 *
 * ErrorBoundary ловит ТОЛЬКО render-path errors. Всё что throws вне
 * React lifecycle — promise rejections, setTimeout callbacks,
 * fetch().then errors без catch — летит мимо ErrorBoundary'а и
 * становится либо console.error либо silent failure.
 *
 * Решение: window.addEventListener('error') + 'unhandledrejection'
 * на main.tsx mount. Emit'им `error.client` с message + stack hash
 * (privacy — не отправляем full path'ы файлов).
 *
 * Debounce — если игра вошла в crash loop (error → render error →
 * boundary → emit), не флудим backend одинаковыми events. Хэшируем
 * stack и эмитим не чаще 1 раза в 10s per unique hash.
 */
import { emit } from './emit.js';

/** Простой FNV-1a hash чтобы не возить crypto.subtle в hot path. */
function hashString(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Возвращаем 8-символьный hex (~32 bit). Достаточно для дедупа.
  return (h >>> 0).toString(16).padStart(8, '0');
}

const recentErrorHashes = new Map<string, number>();
const DEDUPE_WINDOW_MS = 10_000;
/** Hard cap чтобы Map не рос unbounded если приходит 100+ distinct ошибок
 *  в коротком окне. Drop oldest first (Map preserves insertion order). */
const MAX_HASH_ENTRIES = 100;

function shouldReport(hash: string): boolean {
  const now = Date.now();
  const last = recentErrorHashes.get(hash);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;
  recentErrorHashes.set(hash, now);
  // 2026-05-26 BUG FIX: раньше cutoff = DEDUPE_WINDOW_MS * 6 = 60s. Это
  // означало что entries младше 60s оставались. Если 100+ distinct ошибок
  // за минуту — Map всё равно росла (cutoff не покрывал свежие).
  // Теперь: (1) reguлярная очистка по DEDUPE_WINDOW_MS (entries старше
  // дедуп-окна больше не нужны), (2) hard cap MAX_HASH_ENTRIES — если
  // Map переполнена, drop oldest (Map iteration in insertion order).
  if (recentErrorHashes.size > MAX_HASH_ENTRIES / 2) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [k, v] of recentErrorHashes) {
      if (v < cutoff) recentErrorHashes.delete(k);
    }
  }
  while (recentErrorHashes.size > MAX_HASH_ENTRIES) {
    const oldestKey = recentErrorHashes.keys().next().value;
    if (oldestKey === undefined) break;
    recentErrorHashes.delete(oldestKey);
  }
  return true;
}

function reportError(message: string, stack: string | undefined): void {
  const stackHash = hashString(stack ?? message);
  if (!shouldReport(stackHash)) return;
  emit('error.client', {
    error_msg: message.slice(0, 256),
    stack_trace_hash: stackHash,
  });
}

/**
 * Установить global handlers. Idempotent — повторные вызовы no-op.
 * Возвращает cleanup function (для тестов / hot reload).
 */
let installed = false;
export function installGlobalErrorHandlers(): () => void {
  if (installed) return () => undefined;
  installed = true;

  const onError = (event: ErrorEvent): void => {
    const message = event.message || (event.error instanceof Error ? event.error.message : 'error');
    const stack = event.error instanceof Error ? event.error.stack : undefined;
    reportError(message, stack);
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportError(message, stack);
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    installed = false;
  };
}
